import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, fileSrc } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { eur } from "@/components/LineItemsEditor";
import { Input } from "@/components/ui/input";
import { FolderOpen, Loader2, FileDown, Search, FileText, Receipt, Paperclip } from "lucide-react";
import { toast } from "sonner";

const ICONS = { Devis: FileText, Facture: Receipt, Justificatif: Paperclip };

export default function Documents() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/documents").then((r) => setDocs(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false)); }, []);

  const shown = docs.filter((d) => (filter === "all" || d.type === filter) && (!search || (d.title || "").toLowerCase().includes(search.toLowerCase()) || (d.client_name || "").toLowerCase().includes(search.toLowerCase())));

  return (
    <div>
      <PageHeader title="Documents" subtitle="Devis, factures et justificatifs centralisés" icon={FolderOpen} />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (numéro, client)" data-testid="doc-search" className="pl-9 bg-white border-slate-200" />
        </div>
        <div className="flex gap-2">
          {[["all", "Tous"], ["Devis", "Devis"], ["Facture", "Factures"], ["Justificatif", "Justificatifs"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`doc-filter-${k}`} className={`px-3 py-1.5 rounded-lg text-sm border ${filter === k ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "bg-white border-slate-200 text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        : shown.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">Aucun document.</div>
        : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {shown.map((d, i) => {
              const Icon = ICONS[d.type] || FileText;
              return (
                <div key={i} data-testid={`doc-card-${i}`} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-emerald-600" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-400 uppercase tracking-wider">{d.type}</div>
                    <div className="font-medium text-slate-900 truncate">{d.title}</div>
                    <div className="text-xs text-slate-500">{d.date} {d.amount ? `· ${eur(d.amount)}` : ""}</div>
                    {d.client_name && <button onClick={() => d.client_id && navigate(`/clients/${d.client_id}`)} className="text-xs text-emerald-600 hover:underline mt-0.5">{d.client_name}</button>}
                  </div>
                  {d.url && <a href={fileSrc(d.url)} target="_blank" rel="noreferrer" data-testid={`doc-open-${i}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><FileDown className="w-4 h-4" /></a>}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
